// Centralized logging utility
// Replaces console.log statements throughout the application

import { config } from './config';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
  context?: string | undefined;
}

// Sensitive field patterns to mask
const SENSITIVE_PATTERNS = [
  /password/i,
  /secret/i,
  /token/i,
  /apikey/i,
  /api_key/i,
  /authorization/i,
  /bearer/i,
  /credential/i,
  /ssn/i,
  /credit_?card/i,
];

/**
 * Internal helper to recursively mask values based on sensitivity of the current key.
 */
function maskValue(value: any, isSensitiveKey: boolean): any {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    if (!isSensitiveKey) {
      // Non-sensitive keys: keep string as-is for debuggability
      return value;
    }
    // Sensitive keys: mask parts of sensitive strings (show first/last few chars)
    if (value.length > 8) {
      return `${value.substring(0, 3)}...${value.substring(value.length - 3)}`;
    }
    return '***';
  }

  if (Array.isArray(value)) {
    // For arrays, sensitivity is determined by the parent key
    return value.map(item => maskValue(item, isSensitiveKey));
  }

  if (typeof value === 'object') {
    const masked: any = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      // Check if this key matches a sensitive pattern
      const keyIsSensitive = SENSITIVE_PATTERNS.some(pattern => pattern.test(key));
      masked[key] = maskValue(nestedValue, keyIsSensitive);
    }
    return masked;
  }

  return value;
}

/**
 * Mask sensitive data in objects
 */
function maskSensitiveData(data: any): any {
  // At the top level we don't assume sensitivity; it is determined per-key.
  return maskValue(data, false);
}

class Logger {
  private logLevel: LogLevel;
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory

  constructor() {
    this.logLevel = config.development.logLevel;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(level: LogLevel, message: string, context?: string): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    return `${timestamp} [${level.toUpperCase()}]${contextStr} ${message}`;
  }

  private addToBuffer(entry: LogEntry): void {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  private log(level: LogLevel, message: string, data?: any, context?: string): void {
    if (!this.shouldLog(level)) {
      return;
    }

    // Mask sensitive data before logging
    const maskedData = data ? maskSensitiveData(data) : undefined;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data: maskedData,
      context: context || undefined,
    };

    this.addToBuffer(entry);

    const formattedMessage = this.formatMessage(level, message, context);

    // Always log to console in development
    if (config.isDevelopment) {
      switch (level) {
        case 'debug':
          console.debug(formattedMessage, maskedData);
          break;
        case 'info':
          console.log(formattedMessage, maskedData);
          break;
        case 'warn':
          console.warn(formattedMessage, maskedData);
          break;
        case 'error':
          console.error(formattedMessage, maskedData);
          break;
      }
    } else {
      // In production, only log warnings and errors to console
      if (level === 'warn' || level === 'error') {
        console[level](formattedMessage, maskedData);
      }

      // In a real implementation, you would send logs to a logging service
      // this.sendToLoggingService(entry);
    }
  }

  // Public logging methods
  debug(message: string, data?: any, context?: string): void {
    this.log('debug', message, data, context);
  }

  info(message: string, data?: any, context?: string): void {
    this.log('info', message, data, context);
  }

  warn(message: string, data?: any, context?: string): void {
    this.log('warn', message, data, context);
  }

  error(message: string, error?: any, context?: string): void {
    this.log('error', message, error, context);
  }

  // API-specific logging methods
  apiCall(method: string, url: string, data?: any): void {
    this.debug(`API ${method} ${url}`, data, 'API');
  }

  apiResponse(status: number, statusText: string, url: string): void {
    const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
    this.log(level, `API Response ${status} ${statusText} for ${url}`, undefined, 'API');
  }

  apiError(url: string, error: any): void {
    this.error(`API Error for ${url}`, error, 'API');
  }

  // Authentication logging
  authSuccess(method: string, user?: string): void {
    this.info(`Authentication successful via ${method}`, { user }, 'AUTH');
  }

  authError(method: string, error: any): void {
    this.error(`Authentication failed via ${method}`, error, 'AUTH');
  }

  // User action logging
  userAction(action: string, data?: any): void {
    this.info(`User action: ${action}`, data, 'USER');
  }

  // Performance logging
  performance(operation: string, duration: number): void {
    this.debug(`Performance: ${operation} took ${duration}ms`, undefined, 'PERF');
  }

  // Get recent logs (for debugging or admin view)
  getRecentLogs(limit: number = 100): LogEntry[] {
    return this.logs.slice(-limit);
  }

  // Clear logs
  clearLogs(): void {
    this.logs = [];
    this.info('Logs cleared', undefined, 'SYSTEM');
  }

  // Set log level at runtime
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.info(`Log level changed to ${level}`, undefined, 'SYSTEM');
  }

  // Get current log level
  getLogLevel(): LogLevel {
    return this.logLevel;
  }

  // Private method for future integration with logging services
  private async sendToLoggingService(entry: LogEntry): Promise<void> {
    // In a real implementation, you would send logs to services like:
    // - Azure Application Insights
    // - AWS CloudWatch
    // - Datadog
    // - Custom logging endpoint
    
    try {
      // Example implementation:
      // await fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(entry)
      // });
    } catch (error) {
      // Fallback to console if logging service fails
      console.error('Failed to send log to service:', error);
    }
  }
}

// Create and export singleton instance
export const logger = new Logger();

// Export logger instance as default for convenience
export default logger;

// Convenience function for timing operations
export function timeOperation<T>(operation: () => T | Promise<T>, name: string): Promise<T> {
  return new Promise(async (resolve, reject) => {
    const start = performance.now();
    try {
      const result = await Promise.resolve(operation());
      const duration = performance.now() - start;
      logger.performance(name, Math.round(duration));
      resolve(result);
    } catch (error) {
      const duration = performance.now() - start;
      logger.performance(`${name} (failed)`, Math.round(duration));
      logger.error(`Operation failed: ${name}`, error);
      reject(error);
    }
  });
}

// Error boundary logging helper
export function logError(error: Error, errorInfo?: any, context?: string): void {
  logger.error('Unhandled error caught', { error: error.message, stack: error.stack, errorInfo }, context);
}
